import { FeatureKey, LicenseCategory, Application } from './../_models/feature';
import { KubaServices } from './../kuba/kuba.services';
import { BaseServices } from './kuba.services';
import { StorageKeys } from '../_models/storageKeys';
import { IdentityToken } from '../_models/identityToken';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoggedUser } from '../_models/';
import { ThemeService } from '../theme.service';

import {
  Router,
  Event as RouterEvent,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  ActivatedRoute
} from '@angular/router';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';

import { BusinessServices } from './businesses/services/business.services';
import { ViewChild } from '@angular/core';
import { NavigationComponent } from '../_directives/navigation.component';
import { NavigationStateService } from '../shared/services/navigation-state.service';
import { getLoginUrl } from '../shared/common/common-functions.util';

@Component({
  selector: 'kuba-root',
  templateUrl: 'kuba.component.html',
  styleUrls: ['kuba.component.css'],
  providers: [KubaServices],
  standalone: false
})
export class KubaComponent implements OnInit, OnDestroy {
  // Sets initial value to true to show loading spinner on first load
  loading: boolean | any;
  placeholder = true;
  baseUrl: any;
  userSettings: {} | any;
  isReloadPath: boolean | any;
  businessId: number | any;
  licenseCategoryId: number | any;
  showWarningMsg = false;
  @ViewChild(NavigationComponent, { static: false })
  navigationComponent!: NavigationComponent;
  // showWarningMsg = true;
  warningMsg = '';
  private subscriptions: Subscription[] = [];
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private themeService: ThemeService,
    private translate: TranslateService,
    private kubaServices: KubaServices,
    public businessServices: BusinessServices,
    public navigation: NavigationStateService
  ) {
    this.subscriptions.push(
      router.events.subscribe((event: RouterEvent) => {
        this.navigationInterceptor(event);
      })
    );
  }
  reloadNavigationComponent() {
    if (this.navigationComponent) {
      this.navigationComponent.reload(); // Create a reload method in the navigation component
    }
  }
  ngOnInit() {
    this.subscriptions.push(
      this.translate.stream('LICENSE_AGREEMENT_MSG').subscribe(val => {
        this.warningMsg = val.DEMO_INTERNAL_USE;
      })
    );
    if (window.name === 'Remote') {
      this.businessId = +sessionStorage.getItem('RModeBusinessId')!;
    } else {
      this.businessId = BaseServices.BusinessId;
    }
    if (
      BaseServices.ApplicationId === Application.DIN_HMS ||
      BaseServices.ApplicationId === Application.IK
    ) {
      this.subscriptions.push(
        this.businessServices
          .getAgreementByBusinessId(this.businessId)
          .subscribe((result: any) => {
            if (result) {
              this.licenseCategoryId = result.LicenseCategoryId;
              if (
                this.licenseCategoryId === LicenseCategory.DEMO_INTERNAL_USE
              ) {
                this.showWarningMsg = true; // #KW-176
              }
            }
          })
      );
    }

    this.placeholder = false;
    const timeNow = (Date.now() / 1000) | 1;
    if (window.location.pathname.split('/').length > 3) {
      this.isReloadPath = true;

      // Sign this window out when the identity changes in another tab of the
      // same origin (logout, or a different user signing in).
      window.addEventListener('storage', this.onIdentityStorageChange);
    }
    // Themed unconditionally, without the old `userTheme.Stylepath` guard. An
    // application-branded tenant such as Agil has no Theme row to supply a
    // Stylepath, so the guard skipped the call entirely and left those users on
    // Kuba's purple. ThemeService decides what a missing theme means.
    this.themeService.applyThemeForUser(
      BaseServices.userTheme?.Id,
      BaseServices.ApplicationId
    );

    switch (BaseServices.UserRole) {
      case 'Admin':
        this.baseUrl = 'admin/' + BaseServices.ApplicationId;
        this.router.navigate(
          ['admin', BaseServices.ApplicationId, 'dashboard'],
          { relativeTo: this.route }
        );
        break;
      case 'Portal':
        // TODO: need clarification about routing from hameed
        // temporary fix of url implemented
        this.baseUrl = 'portal/' + BaseServices.ApplicationId;
        if (!this.isReloadPath) {
          this.router.navigate(['portal', BaseServices.PortalId, 'dashboard'], {
            relativeTo: this.route
          });
        }
        break;

      case 'Editor':
        this.baseUrl = 'business/' + BaseServices.BusinessId;
        if (!this.isReloadPath) {
          this.router.navigate(
            ['business', BaseServices.BusinessId, 'dashboard'],
            { relativeTo: this.route }
          );
        }
        break;
      case 'Guest':
        this.baseUrl = 'business/' + BaseServices.BusinessId;
        if (
          !this.isReloadPath &&
          (BaseServices.FeatureId === FeatureKey.EMPLOYEES ||
            BaseServices.FeatureId === FeatureKey.CONTACTS)
        ) {
          this.router.navigate(
            ['business', BaseServices.BusinessId, 'dashboard'],
            { relativeTo: this.route }
          );
        } else {
          this.router.navigate([this.baseUrl, 'my-page'], {
            relativeTo: this.route
          });
        }
        break;
      case 'Super':
        this.baseUrl = 'super';
        if (!this.isReloadPath) {
          this.router.navigate(['super', 'dashboard'], {
            relativeTo: this.route
          });
        }
        break;
      case 'Developer':
        this.baseUrl = 'developer';
        if (!this.isReloadPath) {
          this.router.navigate(['developer', 'dashboard'], {
            relativeTo: this.route
          });
        }
        break;
      default:
        this.baseUrl = 'business/' + BaseServices.BusinessId;
        if (!this.isReloadPath) {
          this.router.navigate(
            ['business', BaseServices.BusinessId, 'dashboard'],
            { relativeTo: this.route }
          );
        }
        break;
    }
    // Register this component in the KubaService
    this.kubaServices.setKubaComponent(this);

  }

  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.placeholder = true;
    }
    if (event instanceof NavigationEnd) {
      this.placeholder = false;
    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.placeholder = false;
    }
    if (event instanceof NavigationError) {
      this.placeholder = false;
    }
  }

  /**
   * Cross-tab identity watchdog.
   *
   * The `storage` event fires in every OTHER same-origin window whenever
   * localStorage changes, so this has to be narrow. It used to redirect on any
   * change at all, which broke R mode: the R-mode window is a second window of
   * the same origin, and the periodic token renewal rewrites IDENTITY_TOKEN
   * (renew-token-web only swaps CsrfToken) a second or two after the window
   * opens. That write fired here and bounced the fresh R-mode window to the
   * login page. Ordinary keys — BusinessDetails, languageId, the appraisal
   * autosave — did the same.
   *
   * So: react only to the identity key, and only when the identity actually
   * goes away or changes to a different user.
   */
  private onIdentityStorageChange = (e: StorageEvent): void => {
    // e.key === null means localStorage.clear() — a logout in another tab.
    const isIdentityKey = e.key === null || e.key === StorageKeys.IDENTITY_TOKEN;
    if (!isIdentityKey) {
      return;
    }

    const previousUserId = this.readUserId(e.oldValue);
    const currentUserId = this.readUserId(e.newValue);

    // Same user still signed in — this was a token refresh, not a sign-out.
    if (currentUserId !== null && currentUserId === previousUserId) {
      return;
    }

    // Identity cleared, or replaced by a different user.
    window.location.href = getLoginUrl();
  };

  /** UserId out of a serialised IdentityToken, or null if absent/unparseable. */
  private readUserId(raw: string | null): number | null {
    if (!raw) {
      return null;
    }
    try {
      const token = JSON.parse(raw) as IdentityToken;
      return token?.UserId ?? null;
    } catch {
      return null;
    }
  }

  ngOnDestroy() {
    window.removeEventListener('storage', this.onIdentityStorageChange);
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
